import { Chip, Stack, Typography } from '@common-components';
import { FastForwardOutlined as FastForwardOutlinedIcon } from '@icons';
import { PlacementType } from 'enums';
import { messages } from 'i18n';
import { PartialSubmission, SubmissionMarket } from 'types';
import { PIPE_SEPARATOR } from 'utils/ui-utils';
import { EmailItemType } from 'broker/components/Emails/types';
import { defaultEmailLabelsSelection } from './config';
import InboundEmailLabelsMenu from './InboundEmailLabelsMenu';
import InformationRequestChip from './InformationRequestChip';
import MarketsMenu from './MarketsMenu';
import OutboundEmailLabelsMenu from './OutboundEmailLabelsMenu';
import { EmailListItemAttributes, isCustomerEmail, isInsuredEmail } from './utils';

interface OrganizationLabelsProps {
  submission: PartialSubmission;
  email: EmailListItemAttributes;
}

function OrganizationLabel({ submission, email }: OrganizationLabelsProps) {
  const { agent, carrier, insured } = messages.emailMenus;

  let label = null;
  if (isCustomerEmail(submission, email)) {
    label = submission.placementType === PlacementType.Carrier ? carrier : agent;
  } else if (isInsuredEmail(submission, email)) {
    label = insured;
  }

  if (!label) {
    return null;
  }

  return (
    <Stack flex={1}>
      <Chip label={label} size="small" color="secondary" />
    </Stack>
  );
}

export interface EmailMenusProps {
  email: EmailListItemAttributes;
  emailType: EmailItemType;
  submission: PartialSubmission;
  markets: SubmissionMarket[];
  hideInformationRequestChip?: boolean;
}

export default function EmailMenus({
  email,
  emailType,
  submission,
  markets,
  hideInformationRequestChip,
}: EmailMenusProps) {
  const renderLabelsMenu = () => {
    if (emailType === EmailItemType.SubmissionOutbound) {
      return <OutboundEmailLabelsMenu email={email} selectableLabels={defaultEmailLabelsSelection} />;
    }

    return (
      <InboundEmailLabelsMenu
        submissionId={submission.id}
        email={email}
        selectableLabels={defaultEmailLabelsSelection}
      />
    );
  };

  const isFastTrackEmail = email.labels?.isFastTrack;
  return (
    <Stack direction="row" gap={0.5} maxWidth={1} flexWrap="wrap">
      {isFastTrackEmail && (
        <Chip
          size="small"
          startIcon={FastForwardOutlinedIcon}
          label={messages.general.fastTrack}
          color="green.300"
          rounded={false}
        />
      )}
      {renderLabelsMenu()}
      {!hideInformationRequestChip && <InformationRequestChip email={email} emailType={emailType} />}
      <Typography sx={{ color: 'grey.50', fontSize: '20px' }}>{PIPE_SEPARATOR}</Typography>
      <MarketsMenu email={email} markets={markets} />
      {submission && <OrganizationLabel submission={submission} email={email} />}
    </Stack>
  );
}
